/**
 * Token + device identity, persisted on the device.
 *
 * WHY localStorage and not memory: a phone in a restaurant is backgrounded and
 * killed constantly. An employee who has to log in again every time they put
 * their phone in an apron pocket will stop using the app, and the whole KPI
 * signal disappears with them.
 *
 * WHY the device id is generated here and kept forever: the API's attendance
 * anti-fraud raises UNREGISTERED_DEVICE when no `x-device-id` arrives, and
 * SHARED_DEVICE when two employees clock in from the same one. Both signals
 * are only meaningful if the id is stable across sessions.
 */

const TOKEN_KEY = 'sonauto.staff.tokens.v1';
const DEVICE_KEY = 'sonauto.staff.deviceId.v1';

export interface StoredTokens {
  readonly accessToken: string;
  readonly refreshToken: string;
  readonly expiresAt: number;
}

/** Listeners let the auth context react when a 401 clears the tokens. */
type Listener = (tokens: StoredTokens | null) => void;
const listeners = new Set<Listener>();

export function onTokensChanged(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function emit(tokens: StoredTokens | null): void {
  for (const listener of listeners) listener(tokens);
}

function safeGet(key: string): string | null {
  try {
    return window.localStorage.getItem(key);
  } catch {
    // Private-mode Safari throws on localStorage. The app still works, it just
    // forgets the session when the tab closes.
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* ignore — see safeGet */
  }
}

export function readTokens(): StoredTokens | null {
  const raw = safeGet(TOKEN_KEY);
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const candidate = parsed as Partial<StoredTokens>;
    if (typeof candidate.accessToken !== 'string' || typeof candidate.refreshToken !== 'string') return null;
    return {
      accessToken: candidate.accessToken,
      refreshToken: candidate.refreshToken,
      expiresAt: typeof candidate.expiresAt === 'number' ? candidate.expiresAt : 0,
    };
  } catch {
    return null;
  }
}

export function writeTokens(tokens: StoredTokens): void {
  safeSet(TOKEN_KEY, JSON.stringify(tokens));
  emit(tokens);
}

export function clearTokens(): void {
  try {
    window.localStorage.removeItem(TOKEN_KEY);
  } catch {
    /* ignore */
  }
  emit(null);
}

/**
 * A stable per-install identifier. NOT a security control — it is trivially
 * resettable — but it is exactly what the server's SHARED_DEVICE and
 * UNREGISTERED_DEVICE risk flags are built to consume.
 */
export function deviceId(): string | null {
  const existing = safeGet(DEVICE_KEY);
  if (existing !== null && existing !== '') return existing;
  try {
    // 32 chars max on the server (`x-device-id` is stored in a VARCHAR(32)).
    const generated = crypto.randomUUID().replace(/-/g, '').slice(0, 32);
    safeSet(DEVICE_KEY, generated);
    return generated;
  } catch {
    return null;
  }
}
